import React, { ChangeEvent, useEffect, useState } from 'react';

import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import { ReactComponent as FilterIcon } from 'assets/icons/filter-icon.svg';
import { ReactComponent as FilterIconActive } from 'assets/icons/filter-icon-active.svg';
import { makeStyles } from '@mui/styles';
import { formatEnum } from 'utils/stringUtil';
import { JBColors, JBTheme } from '@juliusbaer-gcrm/gcrm-frontend-components';
import { ColumnDefinition, DataTableWithId, HeadCellFilterType } from 'components/Common/DataTable/DataTableTypes';

const useStyles = makeStyles(() => ({
  filterContainer: {
    position: 'relative',
    display: 'inline-flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  iconButton: {
    padding: 0,
  },
  paper: {
    borderRadius: 0,
    border: `1px solid ${JBColors.app.lightGrey}`,
    minWidth: 144,
    padding: JBTheme.spacing(1, 3),
  },
  label: {
    width: '100%',
  },
  divider: {
    margin: JBTheme.spacing(0, 1),
  },
  textInput: {
    backgroundColor: JBColors.support.greenSmoke[10],
  },
}));

type Props<T> = {
  data: any;
  headCell: ColumnDefinition<T>;
  handleSort: (selectedFilters: { [key: string]: string }[]) => void;
  handleSearch: (searchString: React.SetStateAction<string>, searchKey: keyof T) => void;
  shouldFormatEnum?: boolean;
};

export const createSet = (array: any[], createSetByProperty: any) =>
  Array.from(new Set(array?.map((item: any) => item[createSetByProperty])));

export const DataTableFilter = <T extends DataTableWithId>({
  data,
  headCell,
  handleSort,
  handleSearch,
  shouldFormatEnum = false,
}: Props<T>) => {
  const classes = useStyles();
  const { label, id, filter } = headCell;
  const filterValues = headCell.filterValues ?? createSet(data, id);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<{ [key in keyof T]: string }[]>([]);
  const [inputValue, setInputValue] = React.useState('');

  useEffect(() => {
    // TODO - use generic type

    const selectedFilters: any =
      !!filterValues &&
      filterValues.map((filterValue: any) => {
        return { [id]: filterValue };
      });
    setSelectedFilters(selectedFilters ?? []);
    handleSort(selectedFilters);
  }, []);

  const handleAllChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      // TODO - use generic type

      const selectedFilters: any =
        !!filterValues &&
        filterValues.map((filterValue: any) => {
          return { [id]: filterValue };
        });
      setSelectedFilters(selectedFilters || []);
      handleSort(selectedFilters);
    } else {
      handleSort([]);
      setSelectedFilters([]);
    }
  };

  function handleInputChange(e: { target: { value: React.SetStateAction<string> } }) {
    setInputValue(e.target.value);
    handleSearch(e.target.value, id);
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const name = event.target.name;

    if (checked) {
      // TODO - use generic type

      setSelectedFilters([...(selectedFilters as any), { [id]: name }]);
      // TODO - use generic type

      handleSort([...(selectedFilters as any), { [id]: name }]);
    } else {
      setSelectedFilters(selectedFilters.filter((selectedFilter) => selectedFilter[id] !== name));
      handleSort(selectedFilters.filter((selectedFilter) => selectedFilter[id] !== name));
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box className={classes.filterContainer}>
        {label}
        <IconButton
          aria-haspopup="true"
          id={`${label}-icon`}
          disableRipple
          disableFocusRipple
          className={classes.iconButton}
          onClick={handleClick}
        >
          {filterValues.length === selectedFilters.length ? <FilterIcon /> : <FilterIconActive />}
        </IconButton>
      </Box>
      <Menu
        classes={{ paper: classes.paper }}
        id="filter-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <FormGroup>
          {filter === HeadCellFilterType.SEARCHFILTER && (
            <FormControl fullWidth>
              <TextField
                placeholder={headCell.filterInputPlaceholder ?? 'Search a value...'}
                value={inputValue}
                onChange={(event) => handleInputChange(event)}
                InputProps={{ disableUnderline: true, inputProps: { min: 0 } }}
                className={classes.textInput}
                type={headCell.filterInputType ?? 'text'}
              />
            </FormControl>
          )}

          {filter === HeadCellFilterType.CHECKBOXFILTER && (
            <>
              <MenuItem id="filter-menu-item" data-testid="filter-menu-item">
                <FormControlLabel
                  key="filter-checkbox-all"
                  className={classes.label}
                  control={
                    <Checkbox
                      id="filter-checkbox-all"
                      checked={
                        selectedFilters.length > 0 &&
                        filterValues &&
                        filterValues.length > 0 &&
                        selectedFilters.length === filterValues.length
                      }
                      onChange={handleAllChange}
                      color="primary"
                      name="All"
                      inputProps={
                        { 'data-testid': 'filter-checkbox-all' } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                    />
                  }
                  label="All"
                />
              </MenuItem>
              <Divider className={classes.divider} />

              {filterValues &&
                filterValues.map((filterValue, i) => (
                  <MenuItem
                    key={`filter-checkbox-item-${i}`}
                    id={`filter-checkbox-item-${filterValue}`}
                    data-testid={`filter-item-${filterValue}`}
                  >
                    <FormControlLabel
                      key={`filter-item-label-${i}`}
                      className={classes.label}
                      control={
                        <Checkbox
                          id={`checkbox-${filterValue}`}
                          checked={selectedFilters.some((selectedFilter) => selectedFilter[id] === filterValue)}
                          onChange={handleChange}
                          color="primary"
                          name={filterValue}
                          inputProps={
                            {
                              'data-testid': `checkbox-${filterValue}`,
                            } as React.InputHTMLAttributes<HTMLInputElement>
                          }
                        />
                      }
                      label={shouldFormatEnum ? formatEnum(filterValue) : filterValue}
                    />
                  </MenuItem>
                ))}
            </>
          )}
        </FormGroup>
      </Menu>
    </>
  );
};
